import { useQuery, getTradovateConnections } from "wasp/client/operations";
import useTranslation from "../client/hooks/useTranslation";
import { cn } from "../client/utils";

// Shared source pill: where does this account's data come from?
// Tradovate / MetaTrader carry a sync-health dot; Manual is a muted pill.
// Dot palette mirrors STATUS_STYLES on the account cards.

type Tone = "ok" | "idle" | "warn" | "error";

const TONE_DOT: Record<Tone, string> = {
  ok: "bg-emerald-400",
  idle: "bg-muted-foreground/50",
  warn: "bg-amber-400",
  error: "bg-red-500",
};

function tradovateTone(displayStatus: string | undefined): Tone {
  switch (displayStatus) {
    case "error":
      return "error";
    case "needs_reauth":
    case "foreign_token":
    case "delayed":
      return "warn";
    case "idle":
      return "idle";
    default:
      return "ok"; // active, active_no_accounts, syncing, undefined (loading)
  }
}

function metaTone(status: string | undefined): Tone {
  switch (status) {
    case "error":
      return "error";
    case "pending":
      return "warn";
    case "paused":
    case "deprovisioned":
      return "idle";
    default:
      return "ok"; // idle, active, syncing
  }
}

export default function AccountSourceBadge({
  account,
  className,
}: {
  account: {
    tradovateSyncedAccount?: { id: string } | null;
    metaApiConnection?: { id: string; status?: string } | null;
  };
  className?: string;
}) {
  const t = useTranslation();
  const isTradovate = !!account.tradovateSyncedAccount;
  const isMeta = !isTradovate && !!account.metaApiConnection;
  // User-scoped query — react-query dedupes it across every badge on the page.
  // A user can have several Tradovate connections (one per prop firm); pick the
  // one that owns THIS account so the sync-health dot reflects the right link.
  const { data: tvConnections } = useQuery(getTradovateConnections);
  const tvConnection = (tvConnections ?? []).find((c: any) =>
    (c.syncedAccounts ?? []).some((sa: any) => sa.id === account.tradovateSyncedAccount?.id),
  ) ?? null;

  if (!isTradovate && !isMeta) {
    return (
      <span
        className={cn(
          "inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-muted text-muted-foreground",
          className,
        )}
      >
        {t.tradingAccounts.sourceManual}
      </span>
    );
  }

  const displayStatus = (tvConnection as any)?.displayStatus as string | undefined;
  const tone = isTradovate ? tradovateTone(displayStatus) : metaTone(account.metaApiConnection?.status);
  const label = isTradovate ? t.tradingAccounts.sourceTradovate : t.tradingAccounts.sourceMetaTrader;
  const hint = isTradovate && displayStatus === "idle" ? t.tradovate.statusIdleHint : undefined;

  return (
    <span
      title={hint}
      className={cn(
        "inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-medium border border-border bg-card/50 text-foreground",
        className,
      )}
    >
      <span className={cn("w-1.5 h-1.5 rounded-full shrink-0", TONE_DOT[tone])} />
      {label}
    </span>
  );
}
